import * as React from 'react';
import { Pencil } from 'lucide-react';
import { EMPTY } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { SectionCard } from '../components/SectionCard';
import type { PositionDetail } from '../types';

export interface ContactBlockProps {
  position: PositionDetail;
  canEdit: boolean;
  /** `PATCH /positions/:id` with the three contact fields. */
  onPatch: (patch: Record<string, unknown>) => Promise<unknown>;
}

type Draft = { contactName: string; contactEmail: string; contactPhone: string };

function draftOf(position: PositionDetail): Draft {
  return {
    contactName: position.contactName ?? '',
    contactEmail: position.contactEmail ?? '',
    contactPhone: position.contactPhone ?? '',
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{children}</dd>
    </div>
  );
}

/**
 * The contact person on the intermediary's side for this request (`effectiveContact`): the
 * position's own contact, else the partner's with a "from partner" hint. Hub-internal: never on
 * documents or in supplier messages (decision 59).
 */
export function ContactBlock({ position, canEdit, onPatch }: ContactBlockProps) {
  const contact = position.effectiveContact;
  const partner = position.intermediary;
  const partnerName = partner?.name ?? 'the intermediary';
  const fromPartner = contact?.source === 'partner';

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [draft, setDraft] = React.useState<Draft>(() => draftOf(position));

  const startEdit = () => {
    setDraft(draftOf(position));
    setEditing(true);
  };
  const set = (key: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft((d) => ({ ...d, [key]: e.target.value }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const patch = {
      contactName: draft.contactName.trim() || null,
      contactEmail: draft.contactEmail.trim() || null,
      contactPhone: draft.contactPhone.trim() || null,
    };
    const unchanged =
      patch.contactName === position.contactName &&
      patch.contactEmail === position.contactEmail &&
      patch.contactPhone === position.contactPhone;
    if (unchanged) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onPatch(patch);
      toast.success('Contact saved');
      setEditing(false);
    } catch {
      /* toasted by the caller */
    } finally {
      setSaving(false);
    }
  };

  // When the partner's contact applies, its values are shown as placeholders in the form.
  const placeholder = {
    contactName: fromPartner ? (partner?.contactPerson ?? '') : '',
    contactEmail: fromPartner ? (partner?.email ?? '') : '',
    contactPhone: fromPartner ? (partner?.phone ?? '') : '',
  };

  return (
    <SectionCard
      id="contact"
      title="Contact at intermediary"
      description={`Who to talk to at ${partnerName} about this request. Internal only – never shown on documents or to suppliers.`}
      actions={
        canEdit && !editing ? (
          <Button type="button" variant="outline" size="sm" onClick={startEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {contact && !fromPartner ? 'Edit' : 'Set contact'}
          </Button>
        ) : null
      }
    >
      {editing ? (
        <form className="space-y-3" onSubmit={(e) => void save(e)} aria-label="Edit contact at intermediary">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="contact-name">Name</Label>
              <Input
                id="contact-name"
                autoFocus
                autoComplete="off"
                value={draft.contactName}
                onChange={set('contactName')}
                placeholder={placeholder.contactName || '–'}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-email">E-mail</Label>
              <Input
                id="contact-email"
                type="email"
                autoComplete="off"
                value={draft.contactEmail}
                onChange={set('contactEmail')}
                placeholder={placeholder.contactEmail || '–'}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                type="tel"
                autoComplete="off"
                value={draft.contactPhone}
                onChange={set('contactPhone')}
                placeholder={placeholder.contactPhone || '–'}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Leave all three empty to use the contact of {partnerName}&apos;s partner record.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" loading={saving}>
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : contact ? (
        <div className="space-y-3">
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
            <Field label="Name">
              {contact.name ?? <span className="text-muted-foreground">{EMPTY}</span>}
            </Field>
            <Field label="E-mail">
              {contact.email ? (
                <a
                  href={`mailto:${contact.email}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {contact.email}
                </a>
              ) : (
                <span className="text-muted-foreground">{EMPTY}</span>
              )}
            </Field>
            <Field label="Phone">
              {contact.phone ? (
                <a
                  href={`tel:${contact.phone.replace(/\s+/g, '')}`}
                  className="underline-offset-4 hover:underline"
                >
                  {contact.phone}
                </a>
              ) : (
                <span className="text-muted-foreground">{EMPTY}</span>
              )}
            </Field>
          </dl>
          {fromPartner ? (
            <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline">from partner</Badge>
              <span>Default contact of {partnerName}; no contact set on this request yet.</span>
            </p>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No contact yet
          {partner ? ` – ${partnerName} has no contact person on its partner record either` : ''}.
        </p>
      )}
    </SectionCard>
  );
}
